package pages.inbound

import api.ApiService
import api.dataclasses.inbound.InboundReceipt
import components.autoTable.AutoTableEnums
import components.routing.ApplicationRoutingUrls
import components.routing.ProtectedRoutingProps
import components.tableDateRange.TableDateRangePickers
import ics.materialUI.components.tableFilter.classes.Filter
import csstype.pct
import csstype.px
import ics.materialUI.components.StyledBackDrop
import ics.materialUI.components.autoTable.AutoTable
import ics.materialUI.components.tableFilter.enums.FilterOperatorEnums
import ics.utils.promise.launch
import mui.material.*
import mui.system.sx
import org.w3c.dom.HTMLInputElement
import react.*
import react.dom.events.ChangeEvent
import kotlin.js.Date

val InboundListPage = FC<ProtectedRoutingProps>("InboundListPage") { props ->
    // state hooks
    val (dataState, setDataState) = useState<Array<InboundReceipt>?>(null)
    val (errorMsgState, setErrorMsgState) = useState<String?>(null)
    val (statusesFilteredState, setStatusesFilteredState) = useState<Array<String>?>(null)
    val (checkboxState, setCheckboxState) = useState<Boolean?>(false)
    val (fromDateState, setFromDateState) = useState<Date?>(null)
    val (endDateState, setEndDateState) = useState<Date?>(null)


    // vars
    val possibleStatusDescriptionValues = dataState?.flatMap { listOf(it.statusDescription) }?.distinct()?.toTypedArray() ?: arrayOf()
    val currentStatusFilter = if (statusesFilteredState != null) {
        Filter(
            "statusDescription",
            FilterOperatorEnums.IN,
            statusesFilteredState.joinToString(",")
        )
    } else null

    // handlers
    val handleChangeFilters: (ChangeEvent<HTMLInputElement>) -> Unit = { event ->
        val newState = event.target.value.unsafeCast<Array<String>>()
        setStatusesFilteredState(newState)
    }

    // requests
    val getDataRequest: () -> Unit = {
        launch {
            val response = ApiService.getInboundReceipts(props.session, props.sessionSetter, fromDateState, endDateState)

            if (response.success) {
                setErrorMsgState(null)
                setDataState(response.data)

                response.refreshedSession?.let {
                    props.sessionSetter(it)
                }

            } else {
                setErrorMsgState(response.errorMsg ?: "An unexpected error occurred")
            }
        }
    }

    // functions to be called after render
    useEffect{
        if (dataState == null && errorMsgState.isNullOrEmpty()) getDataRequest()
    }

    // page content
    if (dataState != null) {
        Card {
            elevation = 3

            errorMsgState?.let{
                Alert {
                    severity = AlertColor.error
                    variant = AlertVariant.filled

                    sx {
                        width = 100.pct
                        marginBottom = 10.px
                    }

                    + it
                }
            }

            // status filter dropdown
            FormControl {
                size = Size.small

                sx {
                    margin = 10.px
                    minWidth = 250.px
                    maxWidth = 250.px
                }

                InputLabel {
                    id = "statusFilterSelectLabel"
                    + "Status filter"
                }

                Select {
                    id = "statusFilterSelectField"
                    label = ReactNode("Status filter")
                    labelId = "statusFilterSelectLabel"
                    multiple = true
                    native = false
                    variant = SelectVariant.outlined
                    value = statusesFilteredState ?: arrayOf<String>()
                    renderValue = {
                        ReactNode(statusesFilteredState?.joinToString(", ") ?: "")
                    }
                    onChange = { event, _ ->
                        handleChangeFilters(event)
                    }

                    possibleStatusDescriptionValues.forEach { statusDescription ->
                        MenuItem {
                            key = statusDescription
                            value = statusDescription

                            Checkbox {
                                checked = statusesFilteredState?.any { it == statusDescription } ?: false
                            }

                            ListItemText {
                                primary = Typography.create{
                                    + statusDescription
                                }
                            }
                        }
                    }
                }
            }

            // table date range filters
            TableDateRangePickers {
                checkboxFirst = true
                checkBoxChecked = checkboxState
                checkBoxCheckedSetter = setCheckboxState
                dataSetter = setDataState
                fromDate = fromDateState
                fromDateSetter = setFromDateState
                endDate = endDateState
                endDateSetter = setEndDateState
                darkMode = props.darkMode
            }

            // table
            AutoTable {
                tableData = dataState
                fieldsSequencedToShow = InboundListPageFieldMappings.headerSequence
                headers = InboundListPageFieldMappings.FieldMappings.english
                limit = 25
                limitOptions = arrayOf(25, 50, 100)
                search = true
                dense = true
                link = ApplicationRoutingUrls.Protected.inboundListPage
                linkData = mapOf(AutoTableEnums.PARAM to "recordId")
                export = true
                exportFileName = "Inbound-export-${Date(Date.now()).toISOString()}"
                exportButtonColor = ButtonColor.primary
                filter = if (!statusesFilteredState.isNullOrEmpty()) currentStatusFilter?.let { arrayOf(it) } else null
            }
        }

    } else StyledBackDrop()
}